import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Package,
  FileText,
  ShoppingBag,
  TrendingUp,
  ArrowRight,
  Clock,
  CheckCircle2,
  Sparkles,
  User,
  MapPin,
  ShieldCheck,
  Building2,
  FolderOpen,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatEuro } from "@/lib/catalog";

export const Route = createFileRoute("/_authenticated/account/")({
  head: () => ({
    meta: [
      { title: "Mijn account — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountDashboard,
});

type OrderRow = {
  id: string;
  order_number: string;
  status: "pending" | "processing" | "completed" | "cancelled";
  total_cents: number;
  created_at: string;
  line_items: Array<{ name: string; qty: number }> | null;
};

type QuoteRow = {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  total_cents: number;
  created_at: string;
  valid_until: string | null;
};


function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  accent = "from-[#3a7bd5] to-[#7dd3fc]",
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md">
      <div
        className={`absolute -right-8 -top-8 size-28 rounded-full bg-gradient-to-br ${accent} opacity-10 blur-2xl transition group-hover:opacity-20`}
      />
      <div className="relative flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-text-secondary">
            {label}
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight text-foreground">
            {value}
          </p>
          {sub && <p className="mt-1 text-xs text-text-secondary">{sub}</p>}
        </div>
        <div
          className={`flex size-11 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-lg`}
        >
          <Icon className="size-5" />
        </div>
      </div>
    </div>
  );
}

const orderStatusLabel: Record<OrderRow["status"], { label: string; className: string }> = {
  pending: { label: "In afwachting", className: "bg-amber-50 text-amber-800 border-amber-200" },
  processing: { label: "In behandeling", className: "bg-blue-50 text-blue-800 border-blue-200" },
  completed: { label: "Afgerond", className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  cancelled: { label: "Geannuleerd", className: "bg-red-50 text-red-800 border-red-200" },
};

function AccountDashboard() {
  const { user, profile } = useAuth();

  const { data: orders } = useQuery({
    queryKey: ["dashboard-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, order_number, status, total_cents, created_at, line_items")
        .order("created_at", { ascending: false })
        .returns<OrderRow[]>();
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: quotes } = useQuery({
    queryKey: ["dashboard-quotes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quotes")
        .select("id, quote_number, title, status, total_cents, created_at, valid_until")
        .order("created_at", { ascending: false })
        .returns<QuoteRow[]>();
      if (error) throw error;
      return data ?? [];
    },
  });

  const totalSpent = (orders ?? [])
    .filter((o) => o.status !== "cancelled")
    .reduce((s, o) => s + o.total_cents, 0);
  const openOrders = (orders ?? []).filter(
    (o) => o.status === "pending" || o.status === "processing",
  ).length;
  const openQuotes = (quotes ?? []).filter((q) => q.status === "sent").length;
  const recentOrders = (orders ?? []).slice(0, 4);
  const recentQuotes = (quotes ?? []).slice(0, 3);

  return (
    <div className="space-y-8">

      {/* Page header */}
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          Mijn account
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Welkom terug{profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}. Hier vindt u uw bestellingen, offertes en accountgegevens.
        </p>
      </div>

      {/* Stats */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Totaal besteed"
          value={formatEuro(totalSpent)}
          sub={`${orders?.length ?? 0} bestelling${orders?.length === 1 ? "" : "en"}`}
          icon={TrendingUp}
          accent="from-[#3a7bd5] to-[#7dd3fc]"
        />
        <StatCard
          label="Open bestellingen"
          value={String(openOrders)}
          sub="In afwachting of in behandeling"
          icon={Package}
          accent="from-amber-500 to-orange-400"
        />
        <StatCard
          label="Actieve offertes"
          value={String(openQuotes)}
          sub={`${quotes?.length ?? 0} totaal`}
          icon={FileText}
          accent="from-emerald-500 to-teal-400"
        />
        <StatCard
          label="Uw korting"
          value={`${profile?.discount_percentage ?? 0}%`}
          sub="Op de adviesprijs"
          icon={Sparkles}
          accent="from-violet-500 to-fuchsia-400"
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Recent orders */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border border-border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="font-display text-lg font-bold">Recente bestellingen</h2>
                <p className="text-xs text-text-secondary">Uw laatste activiteit</p>
              </div>
              <Link
                to="/account/bestellingen"
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#3a7bd5] hover:underline"
              >
                Alles bekijken <ArrowRight className="size-3" />
              </Link>
            </div>
            {recentOrders.length === 0 ? (
              <div className="flex flex-col items-center px-5 py-12 text-center">
                <ShoppingBag className="size-10 text-text-secondary/60" />
                <p className="mt-3 text-sm font-medium">Nog geen bestellingen</p>
                <p className="mt-1 text-xs text-text-secondary">
                  Plaats uw eerste order via de webshop.
                </p>
                <Link
                  to="/c"
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#3a7bd5] px-4 py-2 text-xs font-semibold text-white hover:bg-[#2f6ac0]"
                >
                  Bekijk producten <ArrowRight className="size-3.5" />
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {recentOrders.map((o) => {
                  const cfg = orderStatusLabel[o.status];
                  return (
                    <li key={o.id}>
                      <Link
                        to="/account/bestellingen/$id"
                        params={{ id: o.id }}
                        className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-muted/40"
                      >
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0b2a4a]/5 to-[#3a7bd5]/10 text-[#0b2a4a]">
                          <Package className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-sm">
                              #{o.order_number}
                            </span>
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium ${cfg.className}`}
                            >
                              {cfg.label}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-text-secondary">
                            {new Date(o.created_at).toLocaleDateString("nl-NL", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                            {" · "}
                            {o.line_items?.length ?? 0} product
                            {o.line_items?.length === 1 ? "" : "en"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{formatEuro(o.total_cents)}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Profile snapshot + quick actions */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold">Profiel</h2>
              <Link
                to="/account/profiel"
                className="text-xs font-semibold text-[#3a7bd5] hover:underline"
              >
                Bewerken
              </Link>
            </div>
            <dl className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-text-secondary">Naam</dt>
                <dd className="truncate font-medium">{profile?.full_name ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text-secondary">E-mail</dt>
                <dd className="truncate font-medium">{user?.email}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text-secondary">Telefoon</dt>
                <dd className="truncate font-medium">{profile?.phone ?? "—"}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-text-secondary">Status</dt>
                <dd className="font-medium capitalize inline-flex items-center gap-1">
                  {profile?.verification_status === "approved" && (
                    <>
                      <CheckCircle2 className="size-3.5 text-emerald-600" />
                      Geverifieerd
                    </>
                  )}
                  {profile?.verification_status === "pending" && (
                    <>
                      <Clock className="size-3.5 text-amber-600" />
                      In behandeling
                    </>
                  )}
                  {profile?.verification_status === "rejected" && "Afgewezen"}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
            <h2 className="font-display text-lg font-bold">Snelle acties</h2>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <QuickAction to="/account/profiel" icon={User} label="Profiel" />
              <QuickAction to="/account/adressen" icon={MapPin} label="Adressen" />
              <QuickAction
                to="/account/documenten"
                icon={FolderOpen}
                label="Documenten"
              />
              <QuickAction
                to="/account/zakelijk"
                icon={Building2}
                label="Bedrijf"
              />
              <QuickAction
                to="/account/beveiliging"
                icon={ShieldCheck}
                label="Beveiliging"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Recent quotes */}
      <section className="rounded-2xl border border-border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h2 className="font-display text-lg font-bold">Recente offertes</h2>
            <p className="text-xs text-text-secondary">
              Bekijk de status van uw offerteaanvragen
            </p>
          </div>
          <Link
            to="/account/offertes"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#3a7bd5] hover:underline"
          >
            Alles bekijken <ArrowRight className="size-3" />
          </Link>
        </div>
        {recentQuotes.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-text-secondary">
            U heeft nog geen offertes.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {recentQuotes.map((q) => (
              <li key={q.id}>
                <Link
                  to="/account/offertes/$id"
                  params={{ id: q.id }}
                  className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-muted/40"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-400/10 text-emerald-700">
                    <FileText className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-sm">{q.title}</p>
                    <p className="mt-0.5 text-xs text-text-secondary">
                      #{q.quote_number} ·{" "}
                      {new Date(q.created_at).toLocaleDateString("nl-NL")}
                      {q.valid_until &&
                        ` · geldig t/m ${new Date(q.valid_until).toLocaleDateString("nl-NL")}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{formatEuro(q.total_cents)}</p>
                    <span className="mt-0.5 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium capitalize text-text-secondary">
                      {q.status}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function QuickAction({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col items-start gap-2 rounded-xl border border-border bg-background p-3 transition hover:border-[#3a7bd5]/40 hover:bg-[#3a7bd5]/5"
    >
      <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#0b2a4a] to-[#3a7bd5] text-white">
        <Icon className="size-4" />
      </div>
      <span className="text-xs font-semibold">{label}</span>
    </Link>
  );
}
